/* import __COLOCATED_TEMPLATE__ from './navbar.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
/* Nolaneo - I'm ok with the Standalone navbar having direct platform access for now! */
/* eslint-disable @intercom/intercom/no-direct-zendesk-platform-access */
/* eslint-disable @intercom/intercom/no-direct-salesforce-platform-access */
/* eslint-disable @intercom/intercom/no-direct-hubspot-platform-access */
/* eslint-disable @intercom/intercom/no-direct-freshdesk-platform-access */

import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { NavItem, type NavItemInputs, NavTree } from 'embercom/objects/standalone/nav';
import type IntlService from 'ember-intl/services/intl';
import type GreatGuidanceService from 'embercom/services/great-guidance-service';
import type RouterService from '@ember/routing/router-service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type InboxApi from 'embercom/services/inbox-api';
import View from 'embercom/objects/inbox/inboxes/view';
import type SuiteMigrationService from 'embercom/services/suite-migration-service';
import { InboxType } from 'embercom/models/data/inbox/inbox-types';
import type FinVoiceService from 'embercom/services/fin-voice-service';
import type FinStandaloneIntegrationsService from 'embercom/services/fin-standalone-integrations-service';
import type AppService from 'embercom/services/app-service';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import { StandalonePlatformToIcon } from 'embercom/objects/standalone/constants';

const ONBOARDING_WIDGET_MIN_HEIGHT_PX = '76px';
const ONBOARDING_WIDGET_STEPS_COMPLETED_HEIGHT_PX = '116px';

interface Args {}

export default class StandaloneNavbar extends Component<Args> {
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare greatGuidanceService: GreatGuidanceService;
  @service declare router: RouterService;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare inboxApi: InboxApi;
  @service declare suiteMigrationService: SuiteMigrationService;
  @service declare finVoiceService: FinVoiceService;
  @service declare finStandaloneIntegrationsService: FinStandaloneIntegrationsService;

  get showRestrictedNavBar() {
    return this.app.canDisplayExpiredSubscriptionPage;
  }

  get app() {
    return this.appService.app;
  }

  get canUseReactStepBasedIA(): boolean {
    return this.finStandaloneService.platform.canUseReactStepBasedIA;
  }

  get platformIcon(): InterfaceIconName {
    let platform = this.appService.mainStandalonePlatform.platformKey;
    return StandalonePlatformToIcon[platform];
  }

  @tracked links: NavItem[] = [];

  settingsLink: NavItem;

  changelogLink: NavItem;

  intercomSuiteLink: NavItem | undefined;

  manageMigrationLink: NavItem | undefined;

  constructor(owner: unknown, args: Args) {
    super(owner, args);

    let tree = new NavTree(owner);

    let showProcedures = this.app.canUseFinProcedures || !this.app.canUseAiAgentWorkflows;

    let navItems = [
      ...(this.app.canUseStandaloneConnectPage
        ? [
            {
              route: 'apps.app.standalone.connect',
              icon: this.platformIcon,
              labelIntlKey: 'standalone.navbar.connect',
            },
          ]
        : []),
      {
        route: 'apps.app.ask',
        icon: 'fin',
        labelIntlKey: 'standalone.navbar.ask',
        // Show for users with full access OR those on the request-access waitlist path.
        // eslint-disable-next-line object-shorthand
        customFeatureFlagCheck: function () {
          let self = this as NavItem;
          return (
            self.app.canUseFeature('team-rec-internal-agent') ||
            self.app.canUseFeature('team-internal-agent-operator-request-access')
          );
        },
      },
      {
        route: 'apps.app.knowledge-hub.overview',
        icon: 'article',
        labelIntlKey: 'standalone.navbar.train',
        children: [
          {
            route: 'apps.app.knowledge-hub.overview',
            labelIntlKey: 'standalone.navbar.content',
            activeRoutes: [
              'apps.app.knowledge-hub.all-content',
              'apps.app.knowledge-hub.edit',
              'apps.app.knowledge-hub.folder',
              'apps.app.knowledge-hub.loading',
              'apps.app.knowledge-hub.new',
              'apps.app.knowledge-hub.view',
              'apps.app.knowledge-hub.overview',
            ],
          },
          {
            route: 'apps.app.standalone.attributes',
            labelIntlKey: 'standalone.navbar.categories',
            requiredFeatures: ['fin-first-ai-categories'],
          },
          {
            route: 'apps.app.standalone.knowledge.custom-answers',
            labelIntlKey: 'standalone.navbar.custom-answers',
            requiredFeatures: ['team-standalone-custom-answers'],
          },
          {
            route: 'apps.app.standalone.guidance',
            labelIntlKey: 'standalone.navbar.guidance',
            requiredFeatures: ['fin-guidance-closed-beta', 'team-standalone-fin-guidance'],
          },
          {
            route: 'apps.app.standalone.personalisation',
            labelIntlKey: 'standalone.navbar.personalisation',
            requiredFeatures: ['team-ml-fin-personalisation-data-page-all-attributes'],
          },
          {
            route: 'apps.app.standalone.a2a-agents',
            labelIntlKey: 'standalone.navbar.a2a-agents',
            requiredFeatures: ['standalone-a2a-enabled'],
          },
          {
            route: 'apps.app.standalone.escalation',
            labelIntlKey: 'standalone.navbar.escalation',
          },
          ...(showProcedures
            ? [
                {
                  route: 'apps.app.standalone.tasks',
                  labelIntlKey: 'standalone.navbar.procedures',
                },
              ]
            : [
                {
                  route: 'apps.app.standalone.tasks',
                  labelIntlKey: 'standalone.navbar.processes',
                },
              ]),
          {
            route: 'apps.app.standalone.procedures',
            labelIntlKey: 'standalone.navbar.procedures',
            requiredFeatures: ['team-actions-fin-procedures-new-ui'],
          },
        ],
      },
      ...(this.app.canUseSophisticatedTestingCentralisedPage
        ? [
            {
              route: 'apps.app.standalone.testing',
              icon: 'test',
              labelIntlKey: 'standalone.navbar.test',
              requiredFeatures: ['answerbot-fin-playground'],
              children: [
                {
                  route: 'apps.app.standalone.testing',
                  labelIntlKey: 'standalone.navbar.batch-test',
                },
                {
                  route: 'apps.app.standalone.simulations',
                  labelIntlKey: 'standalone.navbar.simulations',
                },
              ],
            },
          ]
        : [
            {
              route: 'apps.app.standalone.testing',
              icon: 'test',
              labelIntlKey: 'standalone.navbar.test',
              requiredFeatures: ['answerbot-fin-playground'],
            },
          ]),
      {
        route: 'apps.app.standalone.deploy',
        icon: 'rocket-ship',
        labelIntlKey: 'standalone.navbar.deploy',
        // eslint-disable-next-line object-shorthand
        customOnClick: function () {
          let self = this as NavItem;
          self.root.setForcedActiveItem(self);
          self.router.transitionTo(`${self.route}.overview`, self.appService.app.id);
        },
        activeRoutes: ['apps.app.standalone.deploy.overview'],
        children: this.deployNavItems,
      },
      {
        route: 'apps.app.standalone.reports',
        icon: 'column-chart',
        labelIntlKey: 'standalone.navbar.report',
        children: [
          ...(this.app.canSeeAiCsatMetrics
            ? [
                {
                  route: 'apps.app.standalone.performance',
                  labelIntlKey: 'standalone.navbar.performance',
                },
              ]
            : [
                {
                  route: 'apps.app.standalone.reports',
                  labelIntlKey: 'standalone.navbar.performance',
                },
              ]),
          {
            route: 'apps.app.standalone.optimize-suggestions',
            labelIntlKey: this.app.canUseFeature('team-self-serve-ai-insights-addon')
              ? 'standalone.navbar.recommendations'
              : 'standalone.navbar.optimize-fin',
          },
          {
            route: 'apps.app.standalone.topics-explorer',
            labelIntlKey: 'standalone.navbar.topics-explorer',
          },
          {
            route: 'apps.app.standalone.topic-trends',
            labelIntlKey: 'standalone.navbar.topic-trends',
          },
          {
            route: 'apps.app.standalone.issues',
            labelIntlKey: 'standalone.navbar.issues',
            requiredFeatures: ['ai-insights-incident-detection-ui'],
          },
          ...(this.app.canSeeAiCsatMetrics
            ? [
                {
                  route: 'apps.app.standalone.reports.custom-report',
                  labelIntlKey: 'standalone.navbar.custom-report',
                  activeRoutes: ['apps.app.standalone.reports'],
                },
              ]
            : []),
          {
            route: 'apps.app.standalone.monitor',
            labelIntlKey: 'standalone.navbar.monitor',
          },
          {
            route: 'inbox.workspace.inbox.inbox',
            labelIntlKey: 'standalone.navbar.conversations',
            // eslint-disable-next-line object-shorthand
            customOnClick: async function () {
              // goes to all fin involved conversations, falls back to all if errors
              let self = this as NavItem;
              try {
                let { inboxes } = await self.inboxApi.listInboxes({ types: [InboxType.View] });

                let aiAgentView = inboxes.find(
                  (inbox: any) =>
                    inbox instanceof View && // needed to acces viewsummary attribute on View which inherits Inbox
                    inbox.viewSummary?.systemType === 'ai_agent_all_conversations',
                );

                if (aiAgentView) {
                  self.router.transitionTo(
                    self.route,
                    self.appService.app.id,
                    'view',
                    aiAgentView.id,
                  );
                } else {
                  self.router.transitionTo(self.route, self.appService.app.id, 'shared', 'all');
                }
              } catch (error) {
                self.router.transitionTo(self.route, self.appService.app.id, 'shared', 'all');
              }
            },
            activeRoutes: ['inbox.workspace'],
          },
        ],
      },
    ] as NavItemInputs[];

    this.links = this.showRestrictedNavBar
      ? []
      : navItems.map((item) => new NavItem(tree, owner, item));

    this.settingsLink = new NavItem(tree, owner, {
      route: 'apps.app.settings',
      icon: 'settings',
      labelIntlKey: 'standalone.navbar.settings',
    });

    this.changelogLink = new NavItem(tree, owner, {
      route: 'apps.app.standalone.changelog',
      icon: 'changelog',
      labelIntlKey: 'standalone.navbar.changelog',
    });

    if (this.app.isMigratingFromStandaloneToSuite) {
      this.intercomSuiteLink = new NavItem(tree, owner, {
        route: 'apps.app.fin-ai-agent',
        icon: 'intercom',
        labelIntlKey: 'standalone.navbar.intercom-suite',
        // eslint-disable-next-line object-shorthand
        customOnClick: function () {
          let self = this as NavItem;
          self.suiteMigrationService.setForceSuiteVersion(true);
          self.router.transitionTo(self.route, self.appService.app.id, {
            queryParams: { suite: '1' },
          });
        },
      });
      if (this.app.canShowManageMigration) {
        this.manageMigrationLink = new NavItem(tree, owner, {
          route: 'apps.app.manage-migration.index',
          icon: 'transfer',
          labelIntlKey: 'standalone.navbar.manage-migration',
          // eslint-disable-next-line object-shorthand
          customOnClick: function () {
            let self = this as NavItem;
            self.router.transitionTo(self.route, self.appService.app.id);
          },
        });
      }
    } else {
      this.suiteMigrationService.setForceSuiteVersion(false);
    }
  }

  get deployNavItems() {
    // For supported integrations use the FinStandaloneIntegrationsService to build the navigation dynamically
    let deployNavItems = this.app.canStandalonePlatformAccessReactIntegrationPages
      ? this.finStandaloneIntegrationsService.navItems
      : [
          {
            route: 'apps.app.standalone.deploy.intercom-messenger',
            labelIntlKey: 'standalone.navbar.intercom-messenger',
            activeRoutes: ['apps.app.standalone.deploy.intercom-messenger.workflow'],
            isLive: () => this.isIntercomMessengerLive,
          },
          {
            route: 'apps.app.standalone.deploy.zendesk.messaging',
            labelIntlKey: 'standalone.navbar.zendesk-messaging',
            platforms: ['zendesk'],
            activeRoutes: ['apps.app.standalone.deploy.zendesk.messaging.workflow'],
            isLive: () => this.isZendeskMessagingLive,
          },
          {
            route: 'apps.app.standalone.deploy.zendesk.tickets',
            labelIntlKey: 'standalone.navbar.zendesk-tickets',
            platforms: ['zendesk'],
            activeRoutes: ['apps.app.standalone.deploy.zendesk.tickets.workflow'],
            isLive: () => this.isZendeskTicketsLive,
          },
          {
            route: 'apps.app.standalone.deploy.freshdesk.tickets',
            labelIntlKey: 'standalone.navbar.freshdesk-tickets',
            platforms: ['freshdesk'],
            activeRoutes: ['apps.app.standalone.deploy.freshdesk.tickets.workflow'],
            isLive: () => this.isFreshdeskTicketsLive,
          },
        ];

    return [
      ...deployNavItems,
      {
        route: 'apps.app.standalone.deploy.fin-agent-api',
        labelIntlKey: 'standalone.navbar.fin-agent-api',
        requiredFeatures: ['fin-agent-api'],
        isLive: () => this.isFinAgentApiLive,
      },
      {
        route: 'apps.app.phone.standalone.deploy',
        labelIntlKey: 'standalone.navbar.phone',
        activeRoutes: ['apps.app.phone.standalone.deploy'],
        requiredFeatures: ['team-standalone-deploy-phone'],
        isLive: () => this.isPhoneLive,
      },
      {
        route: 'apps.app.standalone.deploy.zendesk.copilot',
        labelIntlKey: 'standalone.navbar.zendesk-copilot',
        platforms: ['zendesk'],
        activeRoutes: ['apps.app.standalone.deploy.zendesk.copilot'],
        isLive: () => this.isZendeskCopilotLive,
      },
      {
        route: 'apps.app.standalone.deploy.salesforce.copilot',
        labelIntlKey: 'standalone.navbar.salesforce-copilot',
        platforms: ['salesforce'],
        activeRoutes: ['apps.app.standalone.deploy.salesforce.copilot'],
        requiredFeatures: ['team-standalone-copilot-salesforce'],
        isLive: () => this.isSalesforceCopilotLive,
      },
    ];
  }

  get onboardingWidgetHeight() {
    if (!this.greatGuidanceService.hideFloatingWidget && this.shouldShowOnboardingWidget) {
      if (this.allOnboardingStepsCompleted) {
        return ONBOARDING_WIDGET_STEPS_COMPLETED_HEIGHT_PX;
      }
      let onboardingWidgetHeight = document.getElementById('onboarding-floating-widget-container');
      return onboardingWidgetHeight
        ? `${onboardingWidgetHeight.clientHeight}px`
        : ONBOARDING_WIDGET_MIN_HEIGHT_PX;
    }
    return '0px';
  }

  get isOnGettingStartedRoute() {
    return this.router.currentRouteName.startsWith('apps.app.getting-started');
  }

  get shouldShowOnboardingWidget() {
    return (
      !this.isOnGettingStartedRoute && !this.app.canUseFeature('team-self-serve-ai-onboarding')
    );
  }

  private get onboardingSteps(): any[] {
    return this.greatGuidanceService.steps;
  }

  private get totalOnboardingSteps() {
    return this.onboardingSteps.length;
  }

  private get completedOnboardingSteps() {
    return this.onboardingSteps.filter((step: any) => step.completed).length;
  }

  private get allOnboardingStepsCompleted() {
    return this.completedOnboardingSteps === this.totalOnboardingSteps;
  }

  private get isIntercomMessengerLive() {
    return this.finStandaloneService.intercomMessengerConfig?.isMessengerEnabled ?? false;
  }

  get zendeskPlatform() {
    return this.finStandaloneService.zendeskPlatform;
  }

  get salesforcePlatform() {
    return this.finStandaloneService.salesforcePlatform;
  }

  private get isZendeskMessagingLive() {
    return (
      this.zendeskPlatform.zendeskConfig?.sunshineIntegration?.isEnabledForSunshineMessenger ??
      false
    );
  }

  private get isPhoneLive() {
    return this.finVoiceService.isConnected;
  }

  private get isZendeskTicketsLive() {
    return this.zendeskPlatform.zendeskConfig?.isEnabledForTickets ?? false;
  }

  private get isZendeskCopilotLive() {
    return this.zendeskPlatform.zendeskConfig?.hasInstalledCopilot ?? false;
  }

  private get isSalesforceCopilotLive() {
    return this.salesforcePlatform.salesforceConfig?.hasFinishedCopilotSetup ?? false;
  }

  private get isFreshdeskTicketsLive() {
    return this.finStandaloneService.freshdeskConfig?.isEnabledForTickets ?? false;
  }

  private get isFinAgentApiLive() {
    return this.finStandaloneService.isFinAgentApiLive;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Navbar': typeof StandaloneNavbar;
    'standalone::navbar': typeof StandaloneNavbar;
  }
}
